import dynamic from 'next/dynamic';
import ArrowSplit from '../../atoms/Icons/ArrowSplit';
import Card from '../../atoms/Card';
import { getTodayOpeningHoursData } from '../../../helpers/utils';
import convertStringToSlug from '../../../helpers/utils/convertStringToSlug';
import { useDispatch, useSelector } from 'react-redux';
import eventPhoneNumber from 'ui/lib/gtm/eventPhoneNumber';
import eventDirection from 'ui/lib/gtm/eventDirection';
import eventDataBranch from 'ui/lib/gtm/eventDataBranch';
import { IRootState } from '../../../store';
import Image from 'next/image';
import eventClickDetailBranchSales from '../../../helpers/emarsys/branch-sales/eventClickDetailBranchSales';
import { PAGE_NAME_BRANCH_SALES } from '../../../constants/emarsys/branch-sales';
import { IBreadcrumbs } from '../Breadcrumbs';
import { Button } from '../../atoms';
import { FaWhatsapp } from 'react-icons/fa';
import { sourceCodeWhatsapp, whatsappText } from '../../../constants/whatsappText';
import WhatsappButton from '../../atoms/WhatsappButton';

const PhoneInTalk = dynamic(() => import('ui/components/atoms/Icons/PhoneInTalk'), {
  ssr: false,
});
const GarageHome = dynamic(() => import('ui/components/atoms/Icons/GarageHome'), {
  ssr: false,
});
const Garage = dynamic(() => import('ui/components/atoms/Icons/Garage'), {
  ssr: false,
});
const BodyPaint = dynamic(() => import('ui/components/atoms/Icons/BodyPaint'), {
  ssr: false,
});

interface BranchCardProps {
  data: {
    displayName?: string;
    branchName?: string;
    line1?: string;
    type?: string;
    workingSchedules?: {
      BP?: {
        closed?: boolean;
        openingHour?: string;
        closingHour?: string;
      }[];
      V?: {
        closed?: boolean;
        openingHour?: string;
        closingHour?: string;
      }[];
    };
    city?: {
      name?: string;
      province?: {
        name?: string;
      };
    };
    phone?: string;
    url?: string;
    latitude?: number;
    longitude?: number;
    seoSlug?: string;
  };
  distance: string;
  index: number;
  city?: string;
  branchProvince?: any;
  pageTitle?: string;
  cityCode?: string;
  breadcrumbItems?: IBreadcrumbs[];
  showWhatsappChat?: boolean;
  isMapView?: boolean;
}

const BranchCard = ({
  data = {},
  distance,
  index,
  city,
  branchProvince,
  pageTitle,
  cityCode,
  breadcrumbItems,
  showWhatsappChat = false,
  isMapView = false,
}: BranchCardProps) => {
  const dispatch = useDispatch();
  const { userLocation } = useSelector((state: IRootState) => state.userLocation) || {
    userLocation: {
      latitude: null,
      longitude: null,
    },
  };

  const { userData } = useSelector((state: IRootState) => state.auth) || {
    userData: null,
  };
  const PUBLIC_URL = process.env.NEXT_PUBLIC_URL;

  const services = {
    bodyPaint: (
      <li className="flex gap-2 items-center">
        <div>
          <BodyPaint />
        </div>
        <p className="text-xs text-[#333] md:text-reliableBlack">Body & Cat</p>
      </li>
    ),
    showroom: (
      <li className="flex gap-2 items-center">
        <div>
          <Garage />
        </div>
        <p className="text-xs text-[#333] md:text-reliableBlack">Showroom</p>
      </li>
    ),
    garage: (
      <li className="flex gap-2 items-center">
        <div>
          <GarageHome />
        </div>
        <p className="text-xs text-[#333] md:text-reliableBlack">Bengkel & Suku Cadang</p>
      </li>
    ),
  };

  const typeServices = {
    BP: [services.bodyPaint],
    VSP: [services.showroom, services.garage],
    V: [services.showroom],
  };

  const todayOpeningHoursData = getTodayOpeningHoursData(
    (data.type === 'BP' ? data.workingSchedules?.BP : data.workingSchedules?.V) || []
  );
  const citySlug = convertStringToSlug(data?.city?.name);
  const originDirection = userLocation.latitude
    ? `${userLocation.latitude},${userLocation.longitude}`
    : '';
  const urlDirection = `https://www.google.com/maps/dir/'${originDirection}'/${data.latitude},${data.longitude}`;

  const eventClickEmarsys = (buttonName) => {
    eventClickDetailBranchSales({
      dispatch,
      customerNo: userData?.customerNo,
      breadcrumbItems: breadcrumbItems,
      pageName: PAGE_NAME_BRANCH_SALES.LIST_DEALER_TOYOTA,
      pageTitle: pageTitle,
      sectionName: 'dealer list',
      buttonName: buttonName,
      newParams: {
        branch_name: data?.displayName,
        branch_code: data?.branchName,
        province: branchProvince?.name ?? '',
        province_code: branchProvince?.isocode ?? '',
        city: city,
        city_code: cityCode,
        ...(breadcrumbItems?.[2]?.name
          ? {
              menu_cat_lvl2: breadcrumbItems?.[2]?.name?.toUpperCase(),
            }
          : {}),
      },
    });
  };

  const handleClickCard = (data) => {
    eventClickEmarsys('pilih dealer');
    eventDataBranch(data);
  };

  const generateWhatsappLink = (show: boolean): string => {
    if (!show) return '';

    const whatsappNumber = process.env.NEXT_PUBLIC_WHATSAPP_NUMBER;
    const source = sourceCodeWhatsapp.BRANCH_LISTING;
    const text = whatsappText.NEW_CAR;
    const message = `${text} (${source}-${data?.branchName})`;

    return `https://wa.me/${whatsappNumber}?text=${message}`;
  };

  const chatWhatsappLink = generateWhatsappLink(showWhatsappChat);

  return (
    <Card className="flex flex-col justify-between border-b-[5px] border-reliableBlack5 md:border-none">
      <div>
        <a
          onClick={() => handleClickCard(data)}
          href={PUBLIC_URL + `/dealer-toyota/${citySlug}/${data.seoSlug}`}
        >
          <div className="aspect-video relative">
            <Image
              id="card-branch"
              src={
                data.url
                  ? data.url
                  : 'https://tsohybris.azureedge.net/sys-master-sitmedia/h09/hee/8806171148318/branch.png'
              }
              fill
              alt={data.seoSlug}
              className={`${index === 0 ? 'foto-cabang-1' : 'foto-cabang'}`}
              priority={index === 0}
              quality={70}
            />
          </div>
        </a>
        <div className="pt-2 md:pt-[10px] px-3 md:px-0 pb-4 md:pb-6">
          <a
            onClick={() => handleClickCard(data)}
            href={PUBLIC_URL + `/dealer-toyota/${citySlug}/${data.seoSlug}`}
          >
            <p
              id={`card-branch-${data.displayName?.toLowerCase()?.replace(/ /g, '-')}`}
              className="text-sm font-bold lg:text-lg mb-2 md:mb-2 uppercase"
            >
              {data.displayName}
            </p>
          </a>
          <p className="text-reliableBlack90 text-xs md:text-sm mb-1">{data.line1}</p>
          <div className="flex items-center">
            <ArrowSplit />
            <a
              id="direction-plp"
              onClick={() => {
                eventDirection(
                  data?.displayName,
                  data?.city?.province?.name,
                  data?.city?.name,
                  'plp'
                );
              }}
              href={urlDirection}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-hyperLink"
            >
              Petunjuk Arah{' '}
              {distance && <span className="text-reliableBlack">({distance} Km)</span>}
            </a>
          </div>
          {todayOpeningHoursData && (
            <div className="flex items-center text-xs lg:text-sm mt-4 md:mt-6 md:mb-[10px]">
              <p className="font-semibold">Layanan</p>
              <div
                className={`w-2 h-2 ${
                  todayOpeningHoursData.closed ? 'bg-supportiveRed' : 'bg-[#58A942]'
                } rounded ml-3 mr-[5px]`}
              ></div>
              <p>
                {todayOpeningHoursData.closed ? 'Tutup' : 'Buka'}{' '}
                {!todayOpeningHoursData.closed &&
                  `(${todayOpeningHoursData.openingHour} - ${todayOpeningHoursData.closingHour})`}
              </p>
            </div>
          )}
          <ul className="flex flex-wrap gap-x-7 gap-y-1 text-xs border-t pt-2 mt-2">
            {typeServices[data.type]}
          </ul>
        </div>
      </div>
      <div
        className={`grid gap-2 px-3 md:px-0 ${
          !showWhatsappChat || !isMapView
            ? 'lg:grid-cols-[repeat(auto-fit,minmax(190px,1fr))]'
            : 'grid-cols-1'
        }`}
      >
        {data.phone && (
          <a
            id="call-us-plp"
            aria-label="call us button"
            onClick={() => {
              eventPhoneNumber(data, 'plp');
              eventClickEmarsys('hubungi kami');
            }}
            href={`tel:${data.phone}`}
            className="bg-reliableBlack3 px-4 flex justify-center items-center font-medium flex-1 py-3 mb-2 rounded whitespace-nowrap lg:py-4 lg:mb-0 lg:font-semibold"
          >
            <span className="flex gap-1 items-center min-w-[80px] justify-center text-xs">
              HUBUNGI KAMI
              <PhoneInTalk />
            </span>
          </a>
        )}

        {showWhatsappChat && (
          <WhatsappButton href={chatWhatsappLink}>
            WHATSAPP SALESMAN
            <FaWhatsapp size={20} color="#39AE41" />
          </WhatsappButton>
        )}
      </div>
    </Card>
  );
};

export default BranchCard;
